/**
 * Reusable TipTap rich-text editor with a compact toolbar.
 * Consistent visual shell across every KAPE editor (Lesson, Workbook,
 * Assignment, Marketing, Podcast script, Email templates, Announcements).
 *
 * Emits raw HTML via `onChange(html)`. Persist HTML directly on the
 * asset; render read-only via `dangerouslySetInnerHTML` where needed.
 */
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableCell } from "@tiptap/extension-table-cell";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import { KCIS_ADMIN_PALETTE as PAL } from "../../pages/kcis_admin/KcisAdminShell";

export default function RichEditor({ value, onChange, placeholder = "", testid = "rich-editor" }) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Table.configure({ resizable: true }),
      TableRow, TableHeader, TableCell,
      Link.configure({ openOnClick: false, autolink: true }),
      Image,
      Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
    editorProps: {
      attributes: {
        class: "prose prose-invert max-w-none min-h-[240px] focus:outline-none",
        "data-testid": `${testid}-content`,
      },
    },
  });

  if (!editor) return null;

  const btn = (label, active, run, tid) => (
    <button onClick={run}
            className={`px-2 py-1 text-xs rounded ${active ? "bg-white/10" : ""}`}
            style={{ color: PAL.text }}
            data-testid={`${testid}-${tid}`}>
      {label}
    </button>
  );

  return (
    <div className="rounded-lg border" style={{ borderColor: PAL.border, background: "#0F0B14" }}
         data-testid={testid}>
      <div className="flex flex-wrap gap-1 p-2 border-b" style={{ borderColor: PAL.border }}>
        {btn("B", editor.isActive("bold"), () => editor.chain().focus().toggleBold().run(), "bold")}
        {btn("I", editor.isActive("italic"), () => editor.chain().focus().toggleItalic().run(), "italic")}
        {btn("H2", editor.isActive("heading", { level: 2 }), () => editor.chain().focus().toggleHeading({ level: 2 }).run(), "h2")}
        {btn("H3", editor.isActive("heading", { level: 3 }), () => editor.chain().focus().toggleHeading({ level: 3 }).run(), "h3")}
        {btn("• List", editor.isActive("bulletList"), () => editor.chain().focus().toggleBulletList().run(), "ul")}
        {btn("1. List", editor.isActive("orderedList"), () => editor.chain().focus().toggleOrderedList().run(), "ol")}
        {btn("Quote", editor.isActive("blockquote"), () => editor.chain().focus().toggleBlockquote().run(), "blockquote")}
        {btn("‹›", editor.isActive("codeBlock"), () => editor.chain().focus().toggleCodeBlock().run(), "code")}
        {btn("Table", false, () => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run(), "table")}
        {btn("Link", editor.isActive("link"), () => {
          const url = prompt("URL:");
          if (url) editor.chain().focus().setLink({ href: url }).run();
          else editor.chain().focus().unsetLink().run();
        }, "link")}
        {btn("Image", false, () => {
          const url = prompt("Image URL:");
          if (url) editor.chain().focus().setImage({ src: url }).run();
        }, "image")}
        {btn("↶", false, () => editor.chain().focus().undo().run(), "undo")}
        {btn("↷", false, () => editor.chain().focus().redo().run(), "redo")}
      </div>
      <div className="p-3 max-h-[500px] overflow-y-auto">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
